import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Clock, Send, FileDown, Banknote, Undo2, RotateCw, MessageCircle, Lock } from "lucide-react";
import { api, downloadFile, formatErr } from "@/lib/api";
import { money, fmtDate, fmtDateTime, STATUS } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { siteUrl } from "@/lib/site";
import { Button } from "@/components/ui/button";
import { StatusBadge, Eyebrow } from "@/components/sg/Brand";
import { ChangeOrderButton } from "@/components/workspace/ChangeOrder";
import { openUpgrade } from "@/components/sg/UpgradeDialog";

function sentMessage(r) {
  if (r.email) return "Sent to your client by email";
  if (!r.has_email && r.has_contact) return "Marked as requested. Tap “Text client” to send it from your phone, or add their email so ScopeGuard can email it.";
  if (!r.has_contact) return "Marked as requested. Add the client's email in Client contact so ScopeGuard can notify them — or copy the secure link.";
  return "Requested. We couldn't deliver the email right now; ScopeGuard will retry automatically.";
}

function smsHref(project, m, sender) {
  const link = `${siteUrl()}/portal/${project.client_token}`;
  const body = `Hi ${project.client_name}, quick reminder from ${sender}: "${m.name}" (${money(m.amount)}) is ready for your approval. Review & sign here: ${link}`;
  const sep = /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent) ? "&" : "?";
  return `sms:${project.client_phone}${sep}body=${encodeURIComponent(body)}`;
}

function TextClientButton({ project, m, i }) {
  const { user } = useAuth();
  const allowed = user?.role === "owner" || user?.plan === "super_premium";
  if (!project.client_phone) return null;
  if (!allowed) {
    return (
      <button onClick={() => openUpgrade("Text client")} data-testid={`ms-text-client-locked-${i}`}
        className="inline-flex items-center h-8 px-3 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:text-foreground transition-colors">
        <Lock className="h-3 w-3 mr-1" />Text client
      </button>
    );
  }
  const sender = user.company_name || user.display_name || "your contractor";
  return (
    <a href={smsHref(project, m, sender)} data-testid={`ms-text-client-${i}`}
      onClick={() => { if (!m.sent_at) api.post(`/projects/${project.id}/mark-sent`).catch(() => {}); }}
      className="inline-flex items-center h-8 px-3 rounded-md border border-border text-xs font-medium hover:bg-accent transition-colors">
      <MessageCircle className="h-3 w-3 mr-1" />Text client
    </a>
  );
}

function MilestoneActions({ project, m, i, onChange }) {
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const base = `/projects/${project.id}/milestones/${m.id}`;
  const request = () => run(async () => { const r = await api.post(`${base}/request`); onChange(r.data.project); toast.success(sentMessage(r.data)); });
  const markPaid = () => run(async () => { const r = await api.post(`${base}/mark-paid`); onChange(r.data); toast.success(project.client_email ? "Marked paid. Receipt emailed to client." : "Marked paid."); });
  const undo = () => run(async () => { const r = await api.post(`${base}/undo-paid`); onChange(r.data); });
  const invoice = () => run(() => downloadFile(`${base}/invoice.pdf`, `invoice-${project.id.slice(0, 6)}-${i + 1}.pdf`));
  const btn = "h-8 text-xs";
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {m.status === "pending" && <Button data-testid={`ms-request-${i}`} disabled={busy} onClick={request} size="sm" className={`${btn} bg-indigo-600 hover:bg-indigo-500 text-white`}><Send className="h-3 w-3 mr-1" />Request approval</Button>}
      {["awaiting_signature", "overdue"].includes(m.status) && <Button data-testid={`ms-resend-${i}`} disabled={busy} onClick={request} size="sm" variant="outline" className={btn}><RotateCw className="h-3 w-3 mr-1" />{m.sent_at ? "Resend to client" : "Send to client"}</Button>}
      {["awaiting_signature", "overdue"].includes(m.status) && <TextClientButton project={project} m={m} i={i} />}
      {m.status === "ready_to_invoice" && <Button data-testid={`ms-mark-paid-${i}`} disabled={busy} onClick={markPaid} size="sm" className={`${btn} bg-emerald-600 hover:bg-emerald-500 text-white`}><Banknote className="h-3 w-3 mr-1" />Mark paid</Button>}
      {["ready_to_invoice", "paid"].includes(m.status) && <Button data-testid={`ms-invoice-${i}`} disabled={busy} onClick={invoice} size="sm" variant="outline" className={btn}><FileDown className="h-3 w-3 mr-1" />Invoice PDF</Button>}
      {m.status === "paid" && <Button data-testid={`ms-undo-paid-${i}`} disabled={busy} onClick={undo} size="sm" variant="ghost" className={btn}><Undo2 className="h-3 w-3 mr-1" />Undo</Button>}
    </div>
  );
}

function MilestoneMeta({ m }) {
  if (m.status === "paid") return <div className="text-xs text-muted-foreground mt-1">Signed by {m.signer_name} · paid {fmtDate(m.paid_at)}</div>;
  if (m.status === "ready_to_invoice") return <div className="text-xs text-muted-foreground mt-1">Signed by {m.signer_name} on {fmtDateTime(m.approved_at)}</div>;
  if (["awaiting_signature", "overdue"].includes(m.status)) {
    if (!m.sent_at) return <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">Not sent yet — send it or copy the secure link</div>;
    const rem = [m.r24_sent && "24h", m.r48_sent && "48h"].filter(Boolean);
    return <div className="text-xs text-muted-foreground mt-1">Requested {fmtDateTime(m.sent_at)}{rem.length ? ` · reminders sent: ${rem.join(", ")}` : " · auto-reminders on"}</div>;
  }
  return null;
}

export function MilestoneTimeline({ project, onChange }) {
  return (
    <div className="border border-border rounded-xl bg-card p-6" data-testid="milestone-timeline">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <Eyebrow className="!text-muted-foreground">Milestone timeline</Eyebrow>
        <ChangeOrderButton project={project} onChange={onChange} />
      </div>
      <ol className="relative ml-3">
        {project.milestones.map((m, i) => {
          const last = i === project.milestones.length - 1;
          return (
            <li key={m.id} className="relative pl-8 pb-7 last:pb-0" data-testid={`milestone-${i}`}>
              {!last && <span className={`absolute left-[9px] top-6 bottom-0 w-px ${m.status === "paid" ? "bg-emerald-500/60" : "bg-border"}`} />}
              <span className={`absolute left-0 top-0.5 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-card ${(STATUS[m.status] || STATUS.pending).dot}`}>
                {["paid", "ready_to_invoice"].includes(m.status) ? <Check className="h-3 w-3 text-white" /> : <Clock className="h-3 w-3 text-white" />}
              </span>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h4 className="font-display font-bold text-base md:text-lg">{m.name}{m.change_order && <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 border border-indigo-500/40 rounded px-1.5 py-0.5" data-testid={`ms-change-order-tag-${i}`}>Change order</span>}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.description}</p>
                  <MilestoneMeta m={m} />
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-display text-lg md:text-xl font-bold tabular-nums">{money(m.amount)}</div>
                  <div className="mt-1"><StatusBadge status={m.status} testId={`milestone-status-${i}`} /></div>
                </div>
              </div>
              <MilestoneActions project={project} m={m} i={i} onChange={onChange} />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
